import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { NotificationService } from '../../../../../common/services';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { HubItemFacade } from '../../hub/item/hub-item.facade';
import { CompartmentListFacade } from '../list/compartment-list.facade';
import {
  CompartmentItemCreate,
  CompartmentItemDelete,
  CompartmentItemInitialState,
  CompartmentItemNewCreate,
  CompartmentItemNewSsccId,
  CompartmentItemSetId,
} from './compartment-item.actions';
import { CompartmentItemService } from './compartment-item.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface CompartmentItemStateModel {
  id: number;
  newSsccId: number;
}

const initialState: CompartmentItemStateModel = {
  id: null,
  newSsccId: null,
};

@State<CompartmentItemStateModel>({
  name: 'compartmentItem',
  defaults: initialState,
})
@Injectable()
export class CompartmentItemState {
  constructor(
    private compartmentItemService: CompartmentItemService,
    private compartmentListFacade: CompartmentListFacade,
    private hubItemFacade: HubItemFacade,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: CompartmentItemStateModel): CompartmentItemStateModel {
    return state;
  }

  @Selector()
  static getCompartmentId(state: CompartmentItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getSscdIdForNewCompartment(state: CompartmentItemStateModel): number {
    return state.newSsccId;
  }

  @Action(CompartmentItemSetId)
  setCompartmentId(ctx: StateContext<CompartmentItemStateModel>, { payload }: CompartmentItemSetId): void {
    ctx.patchState({ id: payload });
  }

  @Action(CompartmentItemCreate)
  createNewCompartment(
    ctx: StateContext<CompartmentItemStateModel>,
    { payload }: CompartmentItemCreate,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.compartmentItemService.createNewCompartment(payload).pipe(
      take(1),
      map(() => {
        // todo: create routing dictionary constant
        this.notificationService.success('api.compartment.success.create');
        this.navigateToCompartmentList();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentItemDelete)
  deleteCompartment(
    ctx: StateContext<CompartmentItemStateModel>,
    { payload }: CompartmentItemDelete,
  ): Observable<void | Observable<void> | MatSnackBarRef<SimpleSnackBar>> {
    return this.compartmentItemService.deleteCompartment(payload).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.compartment.success.delete');
        this.navigateToCompartmentList();
        return ctx.dispatch(new CompartmentItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentItemNewSsccId)
  getNewSsccId(
    ctx: StateContext<CompartmentItemStateModel>,
    { payload }: CompartmentItemNewSsccId,
  ): Observable<void | CompartmentItemStateModel | MatSnackBarRef<SimpleSnackBar>> {
    return this.compartmentItemService.getNewSsccId(payload).pipe(
      take(1),
      map((newSsccId: number) => ctx.patchState({ newSsccId })),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(CompartmentItemInitialState)
  setInitialState(ctx: StateContext<CompartmentItemInitialState>): void {
    ctx.setState(initialState);
  }

  private navigateToCompartmentList(): void {
    this.hubItemFacade.hubId$
      .pipe(take(1))
      .subscribe((hubId: number) =>
        this.navigateUserService.navigateUserToAbsolutePath(`/dashboard/administration/hubs/${hubId}/compartments`),
      );
  }

  @Action(CompartmentItemNewCreate)
  createCompartment(
    ctx: StateContext<CompartmentItemStateModel>,
    { payload }: CompartmentItemCreate,
  ): Observable<void | MatSnackBarRef<SimpleSnackBar>> {
    return this.compartmentItemService.createNewCompartment(payload).pipe(
      take(1),
      map(() => {
        // todo: create routing dictionary constant
        this.notificationService.success('api.compartment.success.create');
        this.navigateToCompartmentList();
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }
}
